import { supabase } from '../supabase';

/**
 * Serviço para gerenciar schema do banco de dados
 */
export class SchemaService {
  // Cache de verificação para evitar consultas repetidas
  private isTableVerified = false;
  private tableExists = false;
  private hasAlgorithmColumn = false;
  
  /**
   * Verifica se a tabela de recomendações existe e tem o schema correto
   */
  public async verifyRecommendationsTable(): Promise<{ tableExists: boolean; hasAlgorithmColumn: boolean }> {
    // Se já verificou, retornar resultado em cache
    if (this.isTableVerified) {
      return {
        tableExists: this.tableExists,
        hasAlgorithmColumn: this.hasAlgorithmColumn
      };
    }
    
    try {
      // Forma mais direta de verificar se a tabela existe: tentar consultar a tabela
      const { error } = await supabase
        .from('recommendations')
        .select('id')
        .limit(1);
        
      this.tableExists = !error;
      
      if (!this.tableExists) {
        console.log('Tabela de recomendações não existe ou não está acessível');
        this.isTableVerified = true;
        return { tableExists: false, hasAlgorithmColumn: false };
      }
      
      // Verificar se a coluna algorithm existe, tentando selecioná-la
      const { error: columnError } = await supabase
        .from('recommendations')
        .select('algorithm')
        .limit(1);
        
      this.hasAlgorithmColumn = !columnError;
      this.isTableVerified = true;
      
      return {
        tableExists: this.tableExists,
        hasAlgorithmColumn: this.hasAlgorithmColumn
      };
    } catch (error) {
      console.error('Erro ao verificar schema da tabela:', error);
      return { tableExists: false, hasAlgorithmColumn: false };
    }
  }
  
  /**
   * Cria a tabela de recomendações se não existir
   */
  public async createRecommendationsTableIfNeeded(): Promise<boolean> {
    try {
      const { tableExists } = await this.verifyRecommendationsTable();
      
      if (tableExists) {
        return true;
      }
      
      // Criar a tabela usando RPC para permissões de admin
      const { error } = await supabase.rpc('admin_create_recommendations_table');
      
      if (error) {
        console.error('Erro ao criar tabela de recomendações:', error);
        return false;
      }
      
      // Resetar o cache de verificação
      this.isTableVerified = false;
      
      return true;
    } catch (error) {
      console.error('Erro ao criar tabela de recomendações:', error);
      return false;
    }
  }
  
  /**
   * Adiciona a coluna de algoritmo se não existir
   */
  public async addAlgorithmColumnIfNeeded(): Promise<boolean> {
    try {
      const { tableExists, hasAlgorithmColumn } = await this.verifyRecommendationsTable();
      
      if (!tableExists || hasAlgorithmColumn) {
        return tableExists;
      }
      
      // Adicionar coluna usando RPC para permissões de admin
      const { error } = await supabase.rpc('admin_alter_table_add_algorithm');
      
      if (error) {
        console.error('Erro ao adicionar coluna de algoritmo:', error);
        return false;
      }
      
      // Resetar o cache de verificação
      this.isTableVerified = false;
      
      return true;
    } catch (error) {
      console.error('Erro ao adicionar coluna de algoritmo:', error);
      return false;
    }
  }
  
  /**
   * Cria a função RPC para criação de recomendações
   */
  public async createRecommendationRPC(): Promise<boolean> {
    try {
      console.log('Criando função RPC para recomendações...');
      
      const rpcSQL = `
      CREATE OR REPLACE FUNCTION admin_create_recommendations_table()
      RETURNS boolean
      LANGUAGE plpgsql
      SECURITY DEFINER
      AS $$
      BEGIN
        CREATE TABLE IF NOT EXISTS public.recommendations (
          id UUID PRIMARY KEY,
          user_id UUID REFERENCES auth.users(id) NOT NULL,
          game_id TEXT NOT NULL,
          score REAL DEFAULT 0,
          reason TEXT,
          algorithm TEXT,
          viewed BOOLEAN DEFAULT false,
          liked BOOLEAN,
          game JSONB,
          created_at TIMESTAMP WITH TIME ZONE DEFAULT timezone('utc'::text, now()) NOT NULL,
          updated_at TIMESTAMP WITH TIME ZONE DEFAULT timezone('utc'::text, now()) NOT NULL,
          UNIQUE(user_id, game_id)
        );
        
        -- Adicionar índices para melhorar performance
        CREATE INDEX IF NOT EXISTS recommendations_user_id_idx ON public.recommendations(user_id);
        CREATE INDEX IF NOT EXISTS recommendations_game_id_idx ON public.recommendations(game_id);
        CREATE INDEX IF NOT EXISTS recommendations_viewed_idx ON public.recommendations(viewed);
        CREATE INDEX IF NOT EXISTS recommendations_liked_idx ON public.recommendations(liked);
        
        -- Definir políticas de acesso RLS
        ALTER TABLE public.recommendations ENABLE ROW LEVEL SECURITY;
        
        -- Políticas para usuários normais
        DROP POLICY IF EXISTS "Usuários podem ver suas próprias recomendações" ON public.recommendations;
        CREATE POLICY "Usuários podem ver suas próprias recomendações"
          ON public.recommendations FOR SELECT
          USING (auth.uid() = user_id);
          
        DROP POLICY IF EXISTS "Usuários podem atualizar suas próprias recomendações" ON public.recommendations;
        CREATE POLICY "Usuários podem atualizar suas próprias recomendações"
          ON public.recommendations FOR UPDATE
          USING (auth.uid() = user_id);
          
        -- Políticas para admins
        DROP POLICY IF EXISTS "Admins podem ver todas as recomendações" ON public.recommendations;
        CREATE POLICY "Admins podem ver todas as recomendações"
          ON public.recommendations FOR SELECT
          USING (
            EXISTS (
              SELECT 1 FROM public.profiles
              WHERE id = auth.uid() AND role = 'admin'
            )
          );
          
        DROP POLICY IF EXISTS "Admins podem modificar todas as recomendações" ON public.recommendations;
        CREATE POLICY "Admins podem modificar todas as recomendações"
          ON public.recommendations FOR ALL
          USING (
            EXISTS (
              SELECT 1 FROM public.profiles
              WHERE id = auth.uid() AND role = 'admin'
            )
          );
          
        -- Trigger para atualizar o updated_at
        DROP TRIGGER IF EXISTS set_recommendations_updated_at ON public.recommendations;
        CREATE TRIGGER set_recommendations_updated_at
          BEFORE UPDATE ON public.recommendations
          FOR EACH ROW
          EXECUTE FUNCTION public.set_updated_at();
        
        RETURN TRUE;
      END
      $$;
      `;
      
      const { error } = await this.executeAdminSQL(rpcSQL);
      
      if (error) {
        console.error('Erro ao criar função RPC para recomendações:', error);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Erro ao processar criação de RPC:', error);
      return false;
    }
  }
  
  /**
   * Cria a função RPC para alteração da tabela
   */
  public async createAdminAlterTableRPC(): Promise<boolean> {
    try {
      console.log('Criando função RPC para alteração de tabela...');
      
      const alterTableSQL = `
      CREATE OR REPLACE FUNCTION admin_alter_table_add_algorithm()
      RETURNS boolean
      LANGUAGE plpgsql
      SECURITY DEFINER
      AS $$
      BEGIN
        -- Adicionar coluna algorithm se não existir
        ALTER TABLE public.recommendations 
        ADD COLUMN IF NOT EXISTS algorithm TEXT;
        
        -- Atualizar colunas existentes (exemplo: popular)
        UPDATE public.recommendations 
        SET algorithm = 'popular' 
        WHERE algorithm IS NULL;
        
        RETURN TRUE;
      END
      $$;
      `;
      
      const { error } = await this.executeAdminSQL(alterTableSQL);
      
      if (error) {
        console.error('Erro ao criar função RPC para alteração de tabela:', error);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Erro ao processar criação de RPC para alteração:', error);
      return false;
    }
  }
  
  /**
   * Executa SQL administrativo
   */
  public async executeAdminSQL(sqlCommand: string): Promise<{ error?: any }> {
    try {
      const { error } = await supabase.rpc('exec_sql', { sql: sqlCommand });
      
      if (error) {
        console.error('Erro ao executar SQL administrativo:', error);
        return { error };
      }
      
      return {};
    } catch (error) {
      console.error('Exceção ao executar SQL administrativo:', error);
      return { error };
    }
  }
  
  /**
   * Verifica e corrige o schema de recomendações
   */
  public async verifyAndFixRecommendationsSchema(): Promise<boolean> {
    try {
      console.log('Verificando schema de recomendações...');
      
      // Verificar se tabela existe
      const { tableExists, hasAlgorithmColumn } = await this.verifyRecommendationsTable();
      
      // Se tabela não existe, criar
      if (!tableExists) {
        console.log('Tabela de recomendações não existe, criando...');
        
        // Criar RPC para permitir criação da tabela
        const rpcCreated = await this.createRecommendationRPC();
        if (!rpcCreated) {
          console.error('Falha ao criar RPC para tabela de recomendações');
          return false;
        }
        
        // Criar tabela via RPC
        const tableCreated = await this.createRecommendationsTableIfNeeded();
        if (!tableCreated) {
          console.error('Falha ao criar tabela de recomendações');
          return false;
        }
      }
      
      // Verificar novamente para confirmar se a tabela foi criada
      const { tableExists: tableExistsNow } = await this.verifyRecommendationsTable();
      
      if (!tableExistsNow) {
        console.error('Não foi possível criar tabela de recomendações');
        return false;
      }
      
      // Se não tem coluna algorithm, adicionar
      if (!hasAlgorithmColumn) {
        console.log('Coluna algorithm não existe, criando...');
        
        // Criar RPC para permitir alteração da tabela
        const alterRpcCreated = await this.createAdminAlterTableRPC();
        if (!alterRpcCreated) {
          console.error('Falha ao criar RPC para alteração da tabela');
          return false;
        }
        
        // Adicionar coluna via RPC
        const columnAdded = await this.addAlgorithmColumnIfNeeded();
        if (!columnAdded) {
          console.error('Falha ao adicionar coluna de algoritmo');
          return false;
        }
      }
      
      console.log('Schema de recomendações verificado e corrigido com sucesso');
      return true;
    } catch (error) {
      console.error('Erro ao verificar schema do banco:', error);
      return false;
    }
  }
} 